import Vue from 'vue';
import { Locale } from 'date-fns';

type TLocaleLoader = () => Promise<{ default: Locale }>;

/**
 * App language -> date-fns locale chunk. Each locale is loaded on demand so
 * the main bundle only ever contains the active one.
 */
const LOCALE_LOADERS: Record<string, TLocaleLoader> = {
  en: () => import('date-fns/locale/en-US'),
  es: () => import('date-fns/locale/es'),
  zh: () => import('date-fns/locale/zh-CN'),
  hi: () => import('date-fns/locale/hi'),
  fr: () => import('date-fns/locale/fr'),
  ar: () => import('date-fns/locale/ar-SA'),
  bn: () => import('date-fns/locale/bn'),
  pt: () => import('date-fns/locale/pt'),
  ru: () => import('date-fns/locale/ru'),
  // date-fns ships no Urdu locale; English date wording is the fallback.
  ur: () => import('date-fns/locale/en-US'),
  id: () => import('date-fns/locale/id'),
  de: () => import('date-fns/locale/de'),
  ja: () => import('date-fns/locale/ja'),
  tr: () => import('date-fns/locale/tr'),
  vi: () => import('date-fns/locale/vi'),
  it: () => import('date-fns/locale/it'),
  ca: () => import('date-fns/locale/ca'),
  cs: () => import('date-fns/locale/cs'),
  fi: () => import('date-fns/locale/fi'),
};

/**
 * Mutable registry the timeago plugin reads at render time. Entries are
 * registered lazily by activateDateFnsLocale.
 */
export const dateFnsLocales: Record<string, Locale> = {};

/**
 * Loads (once) the date-fns locale for the given app language and points the
 * global timeago locale at it, re-rendering every mounted <timeago>.
 */
export async function activateDateFnsLocale(lang: string): Promise<void> {
  const loader = LOCALE_LOADERS[lang] || LOCALE_LOADERS.en;
  if (!dateFnsLocales[lang]) {
    try {
      dateFnsLocales[lang] = (await loader()).default;
    } catch {
      // Keep the previously active locale if the chunk fails to load.
      return;
    }
  }
  const timeago = (Vue.prototype as { $timeago?: { locale: string } })
    .$timeago;
  if (timeago) timeago.locale = lang;
}
